import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getStoredToken, removeStoredToken } from '../utils/storage';
import { operationLogger, OperationType } from '../utils/operationLogger';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000/api';

// 创建axios实例
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 默认30秒超时
  headers: {
    'Content-Type': 'application/json',
  },
});

// 创建专用于下载的axios实例（更长的超时时间）
const downloadApiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 下载请求2分钟超时
  headers: {
    'Content-Type': 'application/json',
  },
});

// 通用请求拦截器函数
const createRequestInterceptor = (client: AxiosInstance) => {
  return client.interceptors.request.use(
    (config) => {
      const token = getStoredToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // 记录API请求日志
      const requestId = operationLogger.logAPIRequest(
        config.method?.toUpperCase() || 'GET',
        config.url || '',
        config.data,
        config.headers as Record<string, string>
      );
      
      // 将requestId附加到config中，以便在响应拦截器中使用
      (config as any).metadata = { requestId, startTime: Date.now() };
      
      return config;
    },
    (error) => {
      operationLogger.error(OperationType.API_ERROR, '请求拦截器错误', error);
      return Promise.reject(error);
    }
  );
};

// 为两个客户端添加请求拦截器
createRequestInterceptor(apiClient);
createRequestInterceptor(downloadApiClient);

// 通用响应拦截器函数
const createResponseInterceptor = (client: AxiosInstance) => {
  return client.interceptors.response.use(
    (response: AxiosResponse) => {
      // 记录成功响应日志
      const config = response.config as any;
      if (config.metadata) {
        const duration = Date.now() - config.metadata.startTime;
        operationLogger.logAPIResponse(
          config.metadata.requestId,
          response.status,
          response.data,
          response.headers as Record<string, string>,
          duration
        );
      }
      
      return response;
    },
    (error) => {
      // 记录错误响应日志
      const config = error.config as any;
      if (config?.metadata) {
        const duration = Date.now() - config.metadata.startTime;
        operationLogger.logAPIError(config.metadata.requestId, error, duration);
      }
      
      // 如果是401错误，清除token并重定向到登录页
      if (error.response?.status === 401) {
        // 防止重复清除token
        const currentToken = getStoredToken()
        if (currentToken) {
          removeStoredToken();
          clearAuthToken();
          // 触发全局认证状态更新
          window.dispatchEvent(new CustomEvent('auth:logout'));
          
          operationLogger.warn(OperationType.AUTH_LOGOUT, '检测到401错误，自动登出用户', {
            url: error.config?.url,
            status: error.response?.status
          });
        }
      }
      return Promise.reject(error);
    }
  );
};

// 为两个客户端添加响应拦截器
createResponseInterceptor(apiClient);
createResponseInterceptor(downloadApiClient);

// 通用API请求方法
export const api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return apiClient.get(url, config);
  },

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return apiClient.post(url, data, config);
  },

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return apiClient.put(url, data, config);
  },

  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return apiClient.delete(url, config);
  },

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return apiClient.patch(url, data, config);
  },
};

// 下载专用API方法（更长超时时间）
export const downloadApi = {
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return downloadApiClient.post(url, data, config);
  },
  
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return downloadApiClient.get(url, config);
  },
};

// 设置认证token
export function setAuthToken(token: string): void {
  apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// 清除认证token
export function clearAuthToken(): void {
  delete apiClient.defaults.headers.common['Authorization'];
}

// 导出axios实例供特殊用途使用
export { apiClient };
export default api;